/**
 * AdminTargetsPage — CRO target CRUD admin page.
 *
 * Route: `/admin/cro-targets`
 *
 * Per UI-SPEC §"Admin targets page" anatomy:
 *   - 11-row table: Metric | Current Period | Current Target | Set new target | History | Save
 *   - Inline-edit <input type="number"> with 44×44 touch target (WCAG 2.5.8)
 *   - Per-row ECharts history sparkline (last ~8 versions at 60×24px)
 *   - Loading: 11× Skeleton rows
 *   - Save: POST to /api/cro/admin/targets; on success invalidates scorecard queries
 *   - Save error: inline error row below the failed input
 *
 * Defense-in-depth: the Easy Auth platform gate already filtered non-members;
 * this page additionally checks `useCroMe().isCroGroupMember` and renders a
 * 403-style fallback when it's false (race condition: membership revoked after
 * Easy Auth cached the prior decision).
 *
 * Per THREAT MODEL T-04-09-01: three layers of auth defense:
 *   1. Platform-layer Easy Auth (cached up to 60s)
 *   2. This page's `isCroGroupMember` check (defense-in-depth)
 *   3. Fastify `assertCroGroupMember` on the POST handler
 */
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { AppShell } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { Skeleton } from '../components/ui/Skeleton.js';
import { formatFiscalPeriod } from '../lib/fiscal.js';
import { formatCurrency } from '../lib/format.js';
import { useCroMe } from '../queries/useCroMe.js';
import { useCroTargetMutation } from '../queries/useCroTargetMutation.js';
import { useCroTargets } from '../queries/useCroTargets.js';
import { useCroTargetsStore } from '../store/useCroTargetsStore.js';


import type { CroTargetsListResponse } from '@saledash/shared';

/** The 11 CRO metric IDs in display order (matches UI-SPEC row order). */
const CRO_METRIC_IDS = [
  'CRO-01', 'CRO-02', 'CRO-03', 'CRO-04', 'CRO-05',
  'CRO-06', 'CRO-07', 'CRO-08', 'CRO-09', 'CRO-10', 'CRO-11',
] as const;

/** Human-readable labels per metric (shown in the Metric column). */
const METRIC_LABELS: Record<string, string> = {
  'CRO-01': 'New ARR (Cum YTD)',
  'CRO-02': 'Pipeline Coverage Q2',
  'CRO-03': 'Pipeline Coverage Q3',
  'CRO-04': 'Pipeline Coverage FY',
  'CRO-05': 'Total Pipeline Funnel',
  'CRO-06': 'AE Meetings / Week',
  'CRO-07': 'Active Opps per AE',
  'CRO-08': 'New Business Pipeline',
  'CRO-09': 'Expansion Pipeline',
  'CRO-10': 'Forecast vs Budget',
  'CRO-11': 'Qtr Forecast Variance',
};

/** CRO-05 and CRO-11 have no configurable target (funnel/variance metrics). */
const NO_TARGET_METRICS = new Set(['CRO-05', 'CRO-11']);

type TargetRowType = CroTargetsListResponse['targets'][number];

/** Get the latest-version target row for a given metric. */
function getLatestTarget(
  targets: TargetRowType[],
  metricId: string,
): TargetRowType | null {
  const rows = targets.filter((t) => t.metric_id === metricId && t.is_latest);
  return rows[0] ?? null;
}

/** Get all rows for a metric sorted by version ASC (for history sparkline). */
function getHistoryRows(
  targets: TargetRowType[],
  metricId: string,
): TargetRowType[] {
  return targets
    .filter((t) => t.metric_id === metricId)
    .sort((a, b) => a.version - b.version)
    .slice(-8); // last 8 versions per UI-SPEC
}

/**
 * Mini ECharts sparkline showing the target_value history for a metric.
 * 60px wide × 24px tall, no axes, ink-500 line color per UI-SPEC.
 */
function HistorySparkline({ data }: { data: number[] }): React.JSX.Element | null {
  if (data.length < 2) return null;

  const option = {
    grid: { top: 2, bottom: 2, left: 2, right: 2 },
    xAxis: { type: 'category', show: false, data: data.map((_, i) => String(i)) },
    yAxis: { type: 'value', show: false },
    series: [
      {
        type: 'line',
        data,
        smooth: false,
        symbol: 'none',
        lineStyle: { color: '#94a3b8', width: 1.5 },
        areaStyle: null,
      },
    ],
    animation: false,
  };

  return (
    <ReactECharts
      option={option}
      style={{ width: '60px', height: '24px' }}
      opts={{ renderer: 'canvas' }}
    />
  );
}

/**
 * A single row in the admin targets table.
 */
interface TargetRowProps {
  metricId: string;
  targets: TargetRowType[];
  editingMetricId: string | null;
  editingValue: string;
  setEditing: (metricId: string, currentValue: number) => void;
  updateValue: (s: string) => void;
  cancelEdit: () => void;
  mutate: (body: {
    metric_id: string;
    fiscal_year: number | null;
    fiscal_quarter: number | null;
    target_value: number;
    notes: null;
  }) => void;
  isPending: boolean;
  isError: boolean;
  mutationError: { message: string } | null;
  successMetricId: string | null;
}

function TargetRow({
  metricId,
  targets,
  editingMetricId,
  editingValue,
  setEditing,
  updateValue,
  cancelEdit,
  mutate,
  isPending,
  isError,
  mutationError,
  successMetricId,
}: TargetRowProps): React.JSX.Element {
  const latestTarget = getLatestTarget(targets, metricId);
  const historyRows = getHistoryRows(targets, metricId);
  const historyValues = historyRows.map((r) => r.target_value);
  const isEditing = editingMetricId === metricId;
  const isThisMutationError = isError && editingMetricId === metricId;
  const isSuccess = successMetricId === metricId;

  const currentValue = latestTarget?.target_value ?? 0;
  const isNoTarget = NO_TARGET_METRICS.has(metricId);

  const period = latestTarget
    ? formatFiscalPeriod({
        fiscal_year: latestTarget.fiscal_year,
        fiscal_quarter: latestTarget.fiscal_quarter,
      })
    : '—';

  const handleSave = () => {
    const numVal = parseFloat(editingValue);
    if (isNaN(numVal) || numVal < 0) return;
    mutate({
      metric_id: metricId,
      fiscal_year: latestTarget?.fiscal_year ?? null,
      fiscal_quarter: latestTarget?.fiscal_quarter ?? null,
      target_value: numVal,
      notes: null,
    });
  };

  return (
    <>
      <tr
        data-testid={`target-row-${metricId}`}
        className={`border-b border-ink-100 hover:bg-ink-50 transition-colors ${isSuccess ? 'bg-good-bg/30' : ''}`}
      >
        {/* Metric column */}
        <td className="py-3 px-4 text-sm font-medium text-ink-900">
          <span className="text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500 mr-2">
            {metricId}
          </span>
          {METRIC_LABELS[metricId] ?? metricId}
        </td>

        {/* Current Period */}
        <td className="py-3 px-4 text-sm text-ink-600 tabular-nums">
          {isNoTarget ? '—' : period || 'FY26'}
        </td>

        {/* Current Target */}
        <td className="py-3 px-4 text-sm tabular-nums text-ink-900">
          {isNoTarget ? (
            <span className="text-ink-400 italic text-xs">No target — funnel/variance metric</span>
          ) : latestTarget ? (
            formatCurrency(currentValue)
          ) : (
            <span className="text-ink-400">—</span>
          )}
        </td>

        {/* Set new target (inline edit cell) */}
        <td className="py-3 px-4">
          {isNoTarget ? (
            <span className="text-ink-300 text-xs">—</span>
          ) : isEditing ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="any"
                data-testid={`target-input-${metricId}`}
                className="min-h-[44px] min-w-[44px] w-28 rounded border border-ink-300 px-2 py-1 text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={editingValue}
                onChange={(e) => { updateValue(e.target.value); }}
                autoFocus
              />
              <Button
                variant="primary"
                size="sm"
                data-testid={`save-btn-${metricId}`}
                onClick={handleSave}
                disabled={isPending}
                className="min-h-[44px] min-w-[44px]"
              >
                {isPending ? 'Saving…' : 'Save target'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-testid={`cancel-btn-${metricId}`}
                onClick={cancelEdit}
                disabled={isPending}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <button
              type="button"
              data-testid={`edit-btn-${metricId}`}
              className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-brand-600 transition-colors min-h-[44px] min-w-[44px] px-2 rounded"
              onClick={() => { setEditing(metricId, currentValue); }}
            >
              <span className="text-[0.72rem] text-ink-400">✎</span>
              {latestTarget ? formatCurrency(currentValue) : 'Set new value'}
            </button>
          )}
        </td>

        {/* History sparkline column */}
        <td className="py-3 px-4">
          <HistorySparkline data={historyValues} />
        </td>
      </tr>
      {/* Inline error row below the failed input */}
      {isThisMutationError && mutationError && (
        <tr data-testid={`error-row-${metricId}`}>
          <td colSpan={5} className="px-4 pb-2">
            <div
              className="rounded bg-bad-bg text-bad-text px-3 py-2 text-xs"
              role="alert"
              data-testid="save-error"
            >
              Failed to save: {mutationError.message}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * AdminTargetsPage — the main page component.
 */
export function AdminTargetsPage(): React.JSX.Element {
  const { data: meData, error: meError } = useCroMe();
  // 2026-06-17: useCroMe no longer auto-redirects on 401 (silent so general
  // SF users on apex /main aren't bounced into an Entra sign-in they don't
  // have). Pages that require Easy Auth handle the bounce themselves.
  useEffect(() => {
    if (
      meError &&
      'status' in meError &&
      (meError as { status: number }).status === 401 &&
      typeof window !== 'undefined'
    ) {
      // Easy Auth v2 expects post_login_redirect_uri (not _url) + absolute URL.
      const returnUri = encodeURIComponent(window.location.href);
      window.location.assign(`/.auth/login/aad?post_login_redirect_uri=${returnUri}`);
    }
  }, [meError]);
  const { data: targetsData, isLoading, isError: isFetchError } = useCroTargets();
  const mutation = useCroTargetMutation();

  const { editingMetricId, editingValue, setEditing, updateValue, cancelEdit } =
    useCroTargetsStore();

  // Track which metric just had a successful save for the green flash
  const [successMetricId, setSuccessMetricId] = useState<string | null>(null);

  const targets = useMemo(() => targetsData?.targets ?? [], [targetsData]);

  // Defense-in-depth: check group membership (platform gate already enforced)
  const isMember = meData?.isCroGroupMember ?? true; // assume allowed until proven otherwise (avoids flash)

  // Handle mutation with success tracking
  const handleMutate = (body: {
    metric_id: string;
    fiscal_year: number | null;
    fiscal_quarter: number | null;
    target_value: number;
    notes: null;
  }) => {
    mutation.mutate(body, {
      onSuccess: () => {
        setSuccessMetricId(body.metric_id);
        cancelEdit();
        // Clear the green flash after 1s
        setTimeout(() => { setSuccessMetricId(null); }, 1000);
      },
    });
  };

  // 403 fallback — non-member reached this route
  if (meData !== undefined && !isMember) {
    return (
      <AppShell freshnessSlot={null}>
        <div
          className="flex min-h-[400px] items-center justify-center"
          data-testid="non-member-fallback"
        >
          <Card className="max-w-md text-center space-y-4">
            <div className="text-4xl">🚫</div>
            <h2 className="text-base font-semibold text-ink-900">Access restricted</h2>
            <p className="text-sm text-ink-600">
              You are signed in but not a member of the CRO scorecard group. Contact{' '}
              <strong>Geoff Mueller</strong> for access.
            </p>
            <Link
              to="/"
              className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
            >
              ← Back to scorecard
            </Link>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell freshnessSlot={null}>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-ink-900">CRO Metric Targets</h1>
            <p className="mt-1 text-sm text-ink-500">
              Edit any value below and click Save. Each edit creates a new versioned row;
              prior targets stay queryable for historical trend analysis.
            </p>
          </div>
          <Link
            to="/"
            className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
          >
            ← Scorecard
          </Link>
        </div>

        {/* Loading state: 11× skeleton rows */}
        {isLoading && (
          <Card>
            <div className="space-y-3" data-testid="loading-state">
              {CRO_METRIC_IDS.map((id) => (
                <Skeleton key={id} className="h-8 w-full" data-testid="target-row-skeleton" />
              ))}
            </div>
          </Card>
        )}

        {/* Fetch error state */}
        {isFetchError && (
          <Card>
            <div
              className="flex items-start gap-3 rounded-lg border border-bad bg-bad-bg px-4 py-3 text-sm text-bad-text"
              role="alert"
              data-testid="fetch-error-banner"
            >
              Could not reach the targets service — try refreshing.
            </div>
          </Card>
        )}

        {/* Targets table */}
        {!isLoading && !isFetchError && (
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm" data-testid="targets-table">
              <thead>
                <tr className="border-b border-ink-200 bg-ink-50">
                  <th className="py-3 px-4 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Metric
                  </th>
                  <th className="py-3 px-4 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Current Period
                  </th>
                  <th className="py-3 px-4 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Current Target
                  </th>
                  <th className="py-3 px-4 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Set New Target
                  </th>
                  <th className="py-3 px-4 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    History
                  </th>
                </tr>
              </thead>
              <tbody>
                {CRO_METRIC_IDS.map((metricId) => (
                  <TargetRow
                    key={metricId}
                    metricId={metricId}
                    targets={targets}
                    editingMetricId={editingMetricId}
                    editingValue={editingValue}
                    setEditing={setEditing}
                    updateValue={updateValue}
                    cancelEdit={cancelEdit}
                    mutate={handleMutate}
                    isPending={mutation.isPending && editingMetricId === metricId}
                    isError={mutation.isError}
                    mutationError={mutation.error as { message: string } | null}
                    successMetricId={successMetricId}
                  />
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
